import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  registerStart,
  registerSuccess,
  registerFailure,
  verifyEmailSuccess,
  resetRegistration,
  setRegistrationStep,
  setVerificationEmail
} from '../store/slices/authSlice';
import { registerUser, checkEmailAvailability, checkPhoneAvailability, getVerificationStatus } from '../services/api';
import PasswordStrength from './PasswordStrength';
import EmailVerificationModal from './EmailVerificationModal';
import LanguageSelector from './LanguageSelector';
import './SignUp.css';

function SignUp() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, registrationStep, verificationEmail } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    termsAccepted: false,
    privacyAccepted: false,
    language: i18n.language || 'en'
  });

  const [errors, setErrors] = useState({});
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [phoneAvailable, setPhoneAvailable] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Sync language field with i18n language when it changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, language: i18n.language || 'en' }));
  }, [i18n.language]);

  // Check for pending verification email on component mount
  useEffect(() => {
    const checkPendingVerification = async () => {
      const pendingEmail = localStorage.getItem('pendingVerificationEmail');
      if (pendingEmail) {
        // Verify with backend that email is still pending verification
        try {
          const response = await getVerificationStatus(pendingEmail);
          if (response.data.pending && !response.data.verified) {
            // Email is still pending verification, restore the verification state
            dispatch(setVerificationEmail(pendingEmail));
            dispatch(setRegistrationStep('verification'));
          } else {
            // Email is already verified, clear from localStorage
            localStorage.removeItem('pendingVerificationEmail');
            if (registrationStep === 'verification') {
              dispatch(resetRegistration());
            }
          }
        } catch (error) {
          // If check fails, still show verification screen - better safe than sorry
          console.error('Error checking verification status:', error);
          dispatch(setVerificationEmail(pendingEmail));
          dispatch(setRegistrationStep('verification'));
        }
      }
    };

    checkPendingVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Debounced email check
  useEffect(() => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setEmailAvailable(null);
      // Clear email error when field is empty or invalid
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
      return;
    }

    const timer = setTimeout(async () => {
      setEmailChecking(true);
      try {
        const response = await checkEmailAvailability(formData.email);
        setEmailAvailable(response.data.available);
        if (!response.data.available) {
          setErrors(prev => ({ ...prev, email: t('registration.form.email_taken') }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
        }
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setEmailChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.email, t]);

  // Debounced phone check
  useEffect(() => {
    if (!formData.phone || !/^\+[1-9]\d{6,14}$/.test(formData.phone)) {
      setPhoneAvailable(null);
      // Clear phone error when field is empty or invalid
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
      return;
    }

    const timer = setTimeout(async () => {
      setPhoneChecking(true);
      try {
        const response = await checkPhoneAvailability(formData.phone);
        setPhoneAvailable(response.data.available);
        if (!response.data.available) {
          setErrors(prev => ({ ...prev, phone: t('registration.form.phone_taken') }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.phone;
            return newErrors;
          });
        }
      } catch (error) {
        console.error('Error checking phone:', error);
      } finally {
        setPhoneChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.phone, t]);

  // Debounced password match validation (to prevent excessive re-renders)
  useEffect(() => {
    // Don't validate if confirm password is empty
    if (!formData.confirmPassword) {
      // Clear error when confirm password is empty
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirmPassword;
        return newErrors;
      });
      return;
    }

    // Only validate if both fields have values
    if (!formData.password) {
      return;
    }

    // Debounce the validation to prevent excessive re-renders
    const timer = setTimeout(() => {
      if (formData.password !== formData.confirmPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: t('registration.form.password_mismatch') }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [formData.password, formData.confirmPassword, t]);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = t('registration.errors.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('registration.form.email_error');
    } else if (emailAvailable === false) {
      newErrors.email = t('registration.form.email_taken');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('registration.errors.password_required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('validation.password_too_short');
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = t('validation.password_missing_uppercase');
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = t('validation.password_missing_lowercase');
    } else if (!/[0-9]/.test(formData.password)) {
      newErrors.password = t('validation.password_missing_number');
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      newErrors.password = t('validation.password_missing_special');
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('registration.form.password_mismatch');
    }

    // First name
    if (!formData.firstName || formData.firstName.trim().length < 2) {
      newErrors.firstName = t('registration.errors.first_name_required');
    } else if (formData.firstName.trim().length > 50) {
      newErrors.firstName = t('registration.form.first_name_error');
    }

    // Last name
    if (!formData.lastName || formData.lastName.trim().length < 2) {
      newErrors.lastName = t('registration.errors.last_name_required');
    } else if (formData.lastName.trim().length > 50) {
      newErrors.lastName = t('registration.form.last_name_error');
    }

    // Phone (optional but validate format if provided)
    if (formData.phone && !/^\+[1-9]\d{6,14}$/.test(formData.phone)) {
      newErrors.phone = t('registration.form.phone_error');
    } else if (formData.phone && phoneAvailable === false) {
      newErrors.phone = t('registration.form.phone_taken');
    }

    // Terms and Privacy
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = t('registration.errors.terms_required');
    }
    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = t('registration.errors.privacy_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    dispatch(registerStart());

    try {
      const response = await registerUser({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        phone_number: formData.phone || null,
        terms_accepted: formData.termsAccepted,
        privacy_accepted: formData.privacyAccepted,
        language: formData.language
      });

      dispatch(registerSuccess({ email: response.data.email }));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t('registration.errors.registration_failed');
      dispatch(registerFailure(errorMessage));
      setErrors({ submit: errorMessage });
    }
  };

  const handleVerify = (user) => {
    dispatch(verifyEmailSuccess({ user }));
    // Auto-login after verification
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  const handleResend = async () => {
    // Handled by EmailVerificationModal
  };

  if (registrationStep === 'verification' && verificationEmail) {
    return (
      <EmailVerificationModal
        email={verificationEmail}
        onVerify={handleVerify}
        onResend={handleResend}
        onClose={() => dispatch(resetRegistration())}
      />
    );
  }

  // Check if there's a pending verification email in localStorage
  const pendingVerificationEmail = localStorage.getItem('pendingVerificationEmail');

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <LanguageSelector />
          <h1>{t('registration.title')}</h1>
          <p>{t('registration.subtitle')}</p>
        </div>

        {pendingVerificationEmail && registrationStep === 'form' && (
          <div style={{
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <strong>{t('registration.verification.pending_title', 'Email Verification Pending')}</strong>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                {t('registration.verification.pending_message', 'You have a pending email verification. Click below to continue.')}
              </p>
            </div>
            <button
              onClick={() => {
                dispatch(setVerificationEmail(pendingVerificationEmail));
                dispatch(setRegistrationStep('verification'));
              }}
              style={{
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {t('registration.verification.continue_verification', 'Continue Verification')}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-row">
            <div className="form-group">
              <label>{t('registration.form.first_name')}</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData({ ...formData, firstName: e.target.value });
                  if (errors.firstName) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.firstName;
                      return newErrors;
                    });
                  }
                }}
                placeholder={t('registration.form.first_name_placeholder')}
                className={errors.firstName ? 'error' : ''}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label>{t('registration.form.last_name')}</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData({ ...formData, lastName: e.target.value });
                  if (errors.lastName) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.lastName;
                      return newErrors;
                    });
                  }
                }}
                placeholder={t('registration.form.last_name_placeholder')}
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>{t('registration.form.email')}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.email;
                    return newErrors;
                  });
                }
              }}
              placeholder={t('registration.form.email_placeholder')}
              className={errors.email ? 'error' : ''}
            />
            {emailChecking && <span className="checking">Checking...</span>}
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label>{t('registration.form.password')}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  if (errors.password) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.password;
                      return newErrors;
                    });
                  }
                }}
                placeholder={t('registration.form.password_placeholder')}
                className={errors.password ? 'error' : ''}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            {formData.password && <PasswordStrength password={formData.password} />}
            {errors.password && <span className="error-message">{errors.password}</span>}
            <small>{t('registration.form.password_requirements')}</small>
          </div>

          <div className="form-group">
            <label>{t('registration.form.password_confirm')}</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => {
                setFormData({ ...formData, confirmPassword: e.target.value });
                if (errors.confirmPassword) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.confirmPassword;
                    return newErrors;
                  });
                }
              }}
              onBlur={() => {
                // Validate on blur if both fields have values
                if (formData.password && formData.confirmPassword) {
                  if (formData.password !== formData.confirmPassword) {
                    setErrors(prev => ({ ...prev, confirmPassword: t('registration.form.password_mismatch') }));
                  } else {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.confirmPassword;
                      return newErrors;
                    });
                  }
                }
              }}
              placeholder={t('registration.form.password_confirm_placeholder')}
              className={errors.confirmPassword ? 'error' : ''}
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          <div className="form-group">
            <label>{t('registration.form.phone')}</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => {
                setFormData({ ...formData, phone: e.target.value });
                if (errors.phone) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.phone;
                    return newErrors;
                  });
                }
              }}
              placeholder={t('registration.form.phone_placeholder')}
              className={errors.phone ? 'error' : ''}
            />
            {phoneChecking && <span className="checking">Checking...</span>}
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>

          <div className="checkbox-group">
            <label className={errors.termsAccepted ? 'error-label' : ''}>
              <input
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) => {
                  setFormData({ ...formData, termsAccepted: e.target.checked });
                  if (errors.termsAccepted) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.termsAccepted;
                      return newErrors;
                    });
                  }
                }}
              />
              {t('registration.form.terms')}{' '}
              <Link to="/terms" target="_blank">{t('registration.links.terms_link')}</Link>
            </label>
            {errors.termsAccepted && <span className="error-message">{errors.termsAccepted}</span>}
          </div>

          <div className="checkbox-group">
            <label className={errors.privacyAccepted ? 'error-label' : ''}>
              <input
                type="checkbox"
                checked={formData.privacyAccepted}
                onChange={(e) => {
                  setFormData({ ...formData, privacyAccepted: e.target.checked });
                  if (errors.privacyAccepted) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.privacyAccepted;
                      return newErrors;
                    });
                  }
                }}
              />
              {t('registration.form.privacy')}{' '}
              <Link to="/privacy" target="_blank">{t('registration.links.privacy_link')}</Link>
            </label>
            {errors.privacyAccepted && <span className="error-message">{errors.privacyAccepted}</span>}
          </div>

          {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? t('registration.form.submitting') : t('registration.form.submit')}
          </button>
        </form>

        <div className="signup-footer">
          <p>
            {t('registration.links.already_have_account')}{' '}
            <Link to="/login">{t('registration.links.login_here')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;

